import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { assignDefaultValuesIfNeeded } from './search-engine-container.defaults';
import { SearchFormDataType } from './search-engine-container.props';

import { RANGE_PICKER } from '../../../consts';
import { SearchEngineModel, UseAutocompleteDataReturnModel } from '../../../models';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';

interface UseSearchEngineDataProps<T> {
    autocompleteData: UseAutocompleteDataReturnModel<T>;
    initialFormData?: SearchFormDataType;
    onSearchEngineDataChanged?: (data: SearchEngineModel) => void;
    isWebComponentVersion?: boolean;
    isMapSearch?: boolean;
}

export const useSearchEngineData = <T extends AutocompleteBaseItemModel>({
    autocompleteData,
    initialFormData,
    onSearchEngineDataChanged,
    isWebComponentVersion = false,
    isMapSearch = false,
}: UseSearchEngineDataProps<T>) => {
    const initialFormDataWithDefaults = useMemo(() => {
        const baseDefaults = assignDefaultValuesIfNeeded(initialFormData || {});
        return isMapSearch
            ? ({ ...baseDefaults, geoId: initialFormData?.geoId || '' } as SearchEngineModel)
            : baseDefaults;
    }, [initialFormData, isMapSearch]);

    const [formData, setFormData] = useState(cloneDeep(initialFormDataWithDefaults));

    // Track the last submitted search criteria for map search
    const lastSubmittedRef = useRef<SearchEngineModel | null>(null);
    const isUserInteractingRef = useRef(false);
    const isSubmittingRef = useRef(false);

    const {
        formState: { isSubmitted: isFormSubmitted },
        getValues,
        handleSubmit,
        setValue,
        register,
        reset,
        watch,
    } = useForm<SearchEngineModel>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            ...initialFormDataWithDefaults,
        },
        resetOptions: {
            keepDirtyValues: false,
        },
    });

    const hasSearchCriteriaChanged = useCallback(
        (currentValues: SearchEngineModel) => {
            if (!lastSubmittedRef.current) return true;

            const { location, dates, guests, geoId, flexDates } = currentValues;
            const lastSubmitted = lastSubmittedRef.current;

            return (
                location !== lastSubmitted.location ||
                (isMapSearch && geoId !== lastSubmitted.geoId) ||
                !isEqual(dates, lastSubmitted.dates) ||
                !isEqual(guests, lastSubmitted.guests) ||
                flexDates !== lastSubmitted.flexDates
            );
        },
        [isMapSearch],
    );

    const handleMapSearchDataChange = useCallback(
        (data: SearchEngineModel) => {
            isUserInteractingRef.current = true;

            if (data.location !== getValues('location')) {
                setValue('location', data.location);
            }
            if (data.geoId !== getValues('geoId')) {
                setValue('geoId', data.geoId || '');
            }
            if (!isEqual(data.dates, getValues('dates'))) {
                setValue('dates', data.dates);
            }
            if (!isEqual(data.guests, getValues('guests'))) {
                setValue('guests', data.guests);
            }
            if (data.instantReservation !== getValues('instantReservation')) {
                setValue('instantReservation', data.instantReservation);
            }
            if (data.flexDates !== getValues('flexDates')) {
                setValue('flexDates', data.flexDates ?? RANGE_PICKER.FLEX_DATES.DEFAULT);
            }

            setFormData(cloneDeep(data));
            onSearchEngineDataChanged?.(data);

            setTimeout(() => {
                isUserInteractingRef.current = false;
            }, 10);
        },
        [getValues, setValue, setFormData, onSearchEngineDataChanged],
    );

    const onInputValueUpdated = useCallback(() => {
        const values = cloneDeep(getValues());

        if (isEqual(values, formData)) {
            return;
        }

        const callbackToUse = isMapSearch ? handleMapSearchDataChange : onSearchEngineDataChanged;

        if (isMapSearch) {
            callbackToUse?.(values);
            return;
        }

        const newData = cloneDeep({
            dates: values.dates,
            guests: values.guests,
            instantReservation: values.instantReservation,
            location: values.location,
            flexDates: values.flexDates,
        });

        callbackToUse?.({ ...newData });
        setFormData({ ...newData });
    }, [getValues, formData, isMapSearch, handleMapSearchDataChange, onSearchEngineDataChanged]);

    useEffect(() => {
        if (isMapSearch && initialFormData?.location) {
            setValue('location', initialFormData.location);
        }
    }, [isMapSearch, initialFormData?.location, initialFormData?.geoId, setValue]);

    // Sync form state with initialFormData when it changes (e.g., from props or parent component)
    // This handles React Hook Form timing issues where getValues() may return stale data
    // despite correct defaultValues, especially on mobile devices
    useEffect(() => {
        const currentValues = getValues();
        const shouldResetForm =
            currentValues.location !== initialFormDataWithDefaults.location ||
            (isMapSearch && currentValues.geoId !== initialFormDataWithDefaults.geoId) ||
            !isEqual(currentValues.dates, initialFormDataWithDefaults.dates) ||
            !isEqual(currentValues.guests, initialFormDataWithDefaults.guests) ||
            currentValues.flexDates !== initialFormDataWithDefaults.flexDates;

        if (shouldResetForm && !isUserInteractingRef.current && !isSubmittingRef.current) {
            const currentFlexDates = currentValues.flexDates;
            const resetData = { ...initialFormDataWithDefaults };
            if (
                currentFlexDates !== undefined &&
                currentFlexDates !== RANGE_PICKER.FLEX_DATES.DEFAULT
            ) {
                resetData.flexDates = currentFlexDates;
            }
            reset(resetData);
            setFormData(cloneDeep(resetData));
        }
    }, [initialFormDataWithDefaults, getValues, reset, setFormData, isMapSearch]);

    const onAutocompleteChange = useCallback(
        (value: string) => {
            const oldValue = getValues('location');
            if (oldValue === value) {
                return;
            }

            setValue('location', value);
            onInputValueUpdated();
        },
        [getValues, onInputValueUpdated, setValue],
    );

    const seAutocompleteData = {
        ...autocompleteData,
        onChange: onAutocompleteChange,
        onBlur: useCallback(() => {
            if (formData.location !== getValues('location')) {
                onInputValueUpdated();
            }
        }, [formData.location, getValues, onInputValueUpdated]),
        initialQuery: initialFormData?.location,
    };

    const [isMapSearchSubmitting, setIsMapSearchSubmitting] = useState(false);

    const wrappedHandleSubmit = useCallback(
        (onSubmit: (data: SearchEngineModel) => void) => {
            return handleSubmit((data: SearchEngineModel) => {
                if (isMapSearch) {
                    setIsMapSearchSubmitting(true);
                    isSubmittingRef.current = true;

                    setTimeout(() => {
                        setIsMapSearchSubmitting(false);
                    }, 500);

                    setTimeout(() => {
                        isSubmittingRef.current = false;

                        // Force form reset with the submitted data
                        // to ensure form state is fresh
                        reset(data);
                        setFormData(cloneDeep(data));
                    }, 100);

                    if (hasSearchCriteriaChanged(data)) {
                        lastSubmittedRef.current = { ...data };
                        return onSubmit(data);
                    }

                    return;
                }
                return onSubmit(data);
            });
        },
        [handleSubmit, isMapSearch, hasSearchCriteriaChanged, reset, setFormData],
    );

    const seCommonProps = {
        setValue,
        getValues,
        onInputValueUpdated,
        isMapSearch,
    };

    const isSubmitted = isMapSearch ? isMapSearchSubmitting : isFormSubmitted;

    return {
        getValues,
        handleSubmit: wrappedHandleSubmit,
        reset,
        isSubmitted,
        onInputValueUpdated,
        register,
        seAutocompleteData,
        seDesktopProps: {
            ...seCommonProps,
            autocompleteData: seAutocompleteData,
            register,
            isSubmitted,
            isWebComponentVersion,
        },
        seMobileProps: {
            ...seCommonProps,
            watch,
        },
        watch,
        setValue,
    };
};
